import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, fetchMusic } from '../lib/cms';

export default function Music() {
  const [pageData, setPageData] = useState(null);
  const [albums, setMusic] = useState([]);
  const [loadedEmbeds, setLoadedEmbeds] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadMusicData() {
      const [page, musicData] = await Promise.all([
        fetchPageBySlug('musik'),
        fetchMusic(),
      ]);
      setPageData(page);
      setMusic(musicData);
      setLoading(false);
    }
    loadMusicData();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center bg-sand-50">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-clay-500 border-t-transparent" />
      </div>
    );
  }

  const contentHtml = pageData?.content?.da || '';
  const heroImage = pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/sean-o-406693_1.jpg';

  return (
    <div className="bg-sand-50 text-foreground min-h-screen">
      {/* Header Banner */}
      <section className="relative py-20 md:py-28 bg-[#60b6c0] text-sand-50 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src={`${heroImage}?width=1920&quality=60&format=auto`}
            alt="Heidis Musik"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center flex flex-col gap-4">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold tracking-wide leading-tight">
            Heidi Svelmøes Musik
          </h1>
          <p className="max-w-2xl mx-auto text-sm sm:text-base text-white leading-relaxed">
            Oplev Heidis udgivelser fra hendes debutalbum Audioballerina, til album nr. 2 Talisman, album nr. 3 BTTF med StellarPirate, og senest EP'en Pop Divine med Kamilla Royal
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 md:py-24 bg-white border-b border-sand-200/60">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            {/* Left: Text */}
            <div className="md:col-span-7 text-left">
              <span className="text-xs uppercase tracking-widest text-sage-600 font-bold mb-3 block">
                Udgivelser & Samarbejder
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-wide text-black mb-6">
                UDGIVELSER & SAMARBEJDER
              </h2>
              <div
                className="prose prose-sand prose-headings:font-serif prose-headings:text-black prose-p:text-foreground/80 prose-p:leading-relaxed prose-a:text-clay-600 hover:prose-a:text-clay-800 prose-a:underline hover:prose-a:no-underline max-w-none flex flex-col gap-4 text-base md:text-lg"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            </div>

            {/* Right: Cover Highlight */}
            <div className="md:col-span-5">
              <div className="relative max-w-xs mx-auto flex flex-col gap-4">
                <img
                  src="https://i.scdn.co/image/ab67616d0000b273c4d3cff3b27a568af0642daf"
                  alt="Kamilla Royal – Pop Divine"
                  className="relative z-10 w-full rounded-none shadow-md object-cover aspect-square border border-sand-200"
                />
                <a
                  href="https://open.spotify.com/album/5UaIdYBlzxUjcboPxQwPXa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1DB954] hover:bg-[#1ed760] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold transition-all shadow-sm active:scale-98"
                >
                  <i className="fa-brands fa-spotify text-base" />
                  Hør Pop Divine på Spotify
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Album List */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-2xl mx-auto mb-16 flex flex-col gap-3">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-wide text-black">
              Udgivelser gennem tiden
            </h2>
            <p className="text-sm text-foreground/70">
              Heidi har udgivet tre fuldlængde albums i samarbejde med anerkendte danske og internationale producere og musikere.
            </p>
          </div>

          {/* Albums Stack */}
          <div className="flex flex-col gap-16">
            {[...albums]
              .sort((a, b) => {
                const order = {
                  'breaking-through-the-firewall': 1,
                  'talisman': 2,
                  'audioballerina': 3,
                };
                const orderA = order[a.slug?.da] || 99;
                const orderB = order[b.slug?.da] || 99;
                return orderA - orderB;
              })
              .map((album, index) => {
              const isEven = index % 2 === 0;
              const releaseYear = album.release_date ? new Date(album.release_date).getFullYear() : '';

              return (
                <div
                  key={album.id}
                  className={`flex flex-col md:flex-row gap-8 md:gap-12 items-start bg-white p-6 md:p-10 rounded-none border border-sand-200/60 shadow-sm ${
                    isEven ? '' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Album Cover */}
                  <div className="w-full md:w-1/3 shrink-0 flex flex-col gap-4">
                    <img
                      src={`${album.cover_image?.[0]}?width=400&height=400&fit=cover&format=auto`}
                      alt={album.title.da}
                      className="w-full aspect-square rounded-none object-cover shadow-md border border-sand-200"
                    />

                    {/* Embed Player if present */}
                    {album.embed_code && (
                      <div className="w-full rounded-none overflow-hidden shadow-sm border border-sand-200">
                        {loadedEmbeds[album.id] ? (
                          <iframe
                            src={album.embed_code}
                            width="100%"
                            height="80"
                            frameBorder="0"
                            allowFullScreen=""
                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                            loading="lazy"
                            title={`${album.title.da} player`}
                          ></iframe>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setLoadedEmbeds((prev) => ({ ...prev, [album.id]: true }))}
                            className="w-full py-3 px-4 bg-sand-100 hover:bg-sand-200 text-black text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
                          >
                            <i className="fa-brands fa-spotify text-[#1DB954] text-base" />
                            <span>Indlæs Spotify-afspiller</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Album Details */}
                  <div className="w-full md:w-2/3 text-left flex flex-col gap-4">
                    <div>
                      <div className="flex items-center gap-2 text-xs text-sage-600 font-semibold uppercase tracking-wider">
                        <span>Album</span>
                        {releaseYear && (
                          <>
                            <span>•</span>
                            <span>{releaseYear}</span>
                          </>
                        )}
                      </div>
                      <h3 className="font-serif text-2xl md:text-3xl font-bold text-black mt-1">
                        {album.title.da}
                      </h3>
                    </div>

                    <div
                      className="text-sm md:text-base text-foreground/75 leading-relaxed prose prose-sand"
                      dangerouslySetInnerHTML={{ __html: album.description?.da }}
                    />

                    {/* Music Links */}
                    <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-sand-100">
                      {album.spotify_url && (
                        <a
                          href={album.spotify_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-[#1DB954] hover:bg-[#1ed760] text-white px-5 py-2 text-xs uppercase tracking-wider font-bold transition-all shadow-sm active:scale-98"
                        >
                          <i className="fa-brands fa-spotify text-sm" />
                          Spotify
                        </a>
                      )}
                      {album.apple_music_url && (
                        <a
                          href={album.apple_music_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-[#FC3C44] hover:bg-[#ff4f56] text-white px-5 py-2 text-xs uppercase tracking-wide font-bold"
                        >
                          <i className="fa-brands fa-apple text-sm" />
                          Apple Music
                        </a>
                      )}
                      {album.youtube_url && (
                        <a
                          href={album.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-[#FF0000] hover:bg-[#ff1a1a] text-white px-5 py-2 text-xs uppercase tracking-wide font-bold"
                        >
                          <i className="fa-brands fa-youtube text-sm" />
                          Se video
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
